// Editing service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (editing_posting_params)
//   views      app/views/job_postings/steps/editing/_one.._two.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (editing__one..two)
//   cost       app/models/job_postings/editing_posting.rb (#cost / #editing_style_cost)
//   parity     tasks/post-a-project/parity/writing-variants.md (EDITING)
//
// NO AI consent. editing_style sets the rate (Essential $21 / Line $24 /
// Developmental $28 per 1000 words). subtitle, audience and book_theme are all
// REQUIRED (design marked them optional). The trigger-warning block is RESTORED
// (the design dropped it). niche, english_type and self_edited_before render only
// when the style is NOT Developmental (and are required there). The requirements
// file is REQUIRED.

import { EDITING_STYLES, WRITING_NICHES, ENGLISH_TYPES, RATES } from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const DEVELOPMENTAL = "Developmental Editing"

// Design option descriptions (service-config.js EDIT_DESC). Restored per the
// Plumbline Phase 3 editing appendix (design owns option copy; the renderer
// supports radio-option `sub`). Em dashes converted per the standing no-em-dash
// rule (semantics unchanged).
const EDITING_DESCRIPTIONS = {
  "Essential Editing": "A review for grammar, spelling, syntax, punctuation and inconsistencies.",
  "Line Editing":
    "Everything in Essential, plus a line-by-line review for clarity, coherence, flow, phrasing and sentence variety.",
  "Developmental Editing":
    "A big-picture edit of structure, pacing, character and argument, the deepest level.",
}

// EDITING_STYLES radio options with the design price text "$XX.00 / 1,000 words"
// (EDITING_COST is per-100 words; ×10 = per-1000). Server-traced via ctx.rates
// (ruling C2 / tuw-app:P-001), with the RATES seed as the fallback.
const editingOptions = (ctx = {}) =>
  EDITING_STYLES.map((style) => {
    // Per-key server-rate read: a null server leaf falls back to the RATES seed
    // for THIS style (never null → NaN.toFixed crash, tuw-app:P-001 / TR-001).
    // All three styles are always present in the seed.
    const rate = ctx.rates?.editing_cost?.[style] ?? RATES.EDITING_COST[style]
    return {
      value: style,
      label: style,
      price: `$${(rate * 10).toFixed(2)} / 1,000 words`,
      sub: EDITING_DESCRIPTIONS[style],
    }
  })

const notDevelopmental = (values) => values.editing_style !== DEVELOPMENTAL

export const editing = {
  key: "editing",
  postingKey: "editing_posting",
  name: "Editing",

  steps: () => [
    // Step 1 — Editing style (editing__one).
    {
      id: "style",
      title: "Editing style",
      sub: "Pick the level of edit your manuscript needs. Price is per 1,000 words.",
      fields: [
        {
          type: "radio",
          key: "editing_style",
          required: true,
          options: (v = {}, ctx = {}) => editingOptions(ctx),
        },
      ],
    },

    // Step 2 — About your manuscript (editing__two). Field order restored to the
    // design (audience/book_theme are backend-required; design owns copy and
    // order, while the backend owns the required flags).
    {
      id: "manuscript",
      title: "About your manuscript",
      sub: "Tell your editor what they’re working with.",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          help: "Minimum 1,000, in increments of 500.",
          required: true,
          min: 1000,
          step: 500,
        },
        {
          type: "textarea",
          key: "audience",
          label: "Intended audience",
          required: true,
          rows: 3,
        },
        {
          type: "textarea",
          key: "book_theme",
          label: "Themes or tropes your book explores",
          required: true,
          rows: 3,
        },
        {
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          when: notDevelopmental,
          blankLabel: "Select Niche/Genre",
          options: asOptions([...WRITING_NICHES].sort()),
        },
        {
          type: "select",
          key: "english_type",
          label: "US or UK English?",
          required: true,
          when: notDevelopmental,
          blankLabel: "Select English type",
          options: ENGLISH_TYPES,
        },
        {
          type: "toggle",
          key: "self_edited_before",
          label: "I’ve self-edited or had developmental editing done",
          when: notDevelopmental,
          detailsKey: "self_edited_before_details",
          detailsType: "textarea",
          detailsLabel: "Tell us about that",
        },
        {
          type: "toggle",
          key: "nsfw",
          label: "Topic is not safe for work",
          detailsKey: "nsfw_details",
          detailsType: "textarea",
          detailsLabel: "Reason topic is NSFW",
          detailsRequired: true,
        },
        {
          // Kept per ruling C10 (backend accepts trigger_warning + details); copy
          // restyled to the design's trigger-toggle idiom (fTriggerToggle).
          type: "triggerWarning",
          label: "May contain sensitive / triggering topics",
        },
        {
          type: "textarea",
          key: "additional_info",
          label: "Additional information",
          required: false,
          rows: 4,
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          help: "Required. Supported file types: .doc, .docx",
          required: true,
          accept: ".doc,.docx",
          placeholder: "Upload your manuscript",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (editing_posting.rb#cost):
  //   (word_count / 100.0) * editing_style_cost   (FLOAT division)
  // editing_style_cost = EDITING_COST[style] (Essential 2.10 / Line 2.40 / Devel 2.80).
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const rate = ctx.rates?.editing_cost?.[values.editing_style] ?? RATES.EDITING_COST[values.editing_style] ?? 0
    const lines = []

    if (wc > 0 && rate > 0) {
      lines.push({
        label: `${values.editing_style} (${wc.toLocaleString()} words)`,
        amount: money((wc / 100) * rate),
      })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // editing_posting = [word_count, editing_style]. Null until both are set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (wc <= 0 || !values.editing_style) return null

    return {
      postingType: "editing_posting",
      wordCount: wc,
      editingStyle: values.editing_style,
    }
  },
}
